/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    IOrderConfirmationViewProps,
    IHelp,
    IOrderSummary,
    IPaymentMethods,
    IGroup,
    IGroupDelivery,
    IGroups,
    IOrderInformation,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { find } from 'lodash';

import { getCartState } from '@msdyn365-commerce/global-state';
import { trackPurchase } from '../utilities/datalayer';

interface ExtensionProperty {
    Key: string;
    Value?: {
        BooleanValue?: boolean;
        [key: string]: unknown;
    };
}

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {channelReferenceId}
        {receiptEmail}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo,
    pickupDateTimeslot
}) => {
    const addressWithExtendedProps =
        address && React.isValidElement(address)
            ? React.cloneElement(address, {
                  ...(address.props as any),
                  address: {
                      ...(address.props as any).address,
                      FullAddress: (address.props as any).address?.FullAddress?.replace('DEF', '')?.trim(),
                      State: ''
                  }
              })
            : address;

    return (
        <Node {...deliveryProps}>
            {heading}
            {addressWithExtendedProps}
            {pickupDateTimeslot}
        </Node>
    );
};

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {delivery && delivery.shippingItemsToYou}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

const _renderCustomTaxLine = (taxAmount: number): JSX.Element | null => {
    //const { estimatedTaxLabel, yetToBeCalculatedText} = props.resources;

    return (
        <div style={{ marginBottom: '1rem' }}>
            <p className='ms-order-confirmation__order-summary-line-tax-amount'>
                <span className='ms-order-confirmation__order-summary-label'>Tax</span>
                <span className='msc-price ms-order-confirmation__order-summary-price'>
                    <span className='msc-price__actual' itemProp='price'>
                        {taxAmount}
                    </span>
                </span>
            </p>
        </div>
    );
};

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    ...other
}) => {
    // @ts-ignore
    const locale = other.locale;
    // @ts-ignore
    const taxAmount = other.taxAmount;

    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {shipping}
            {locale.toLowerCase() == 'en-gb' && taxAmount > 0 ? _renderCustomTaxLine(taxAmount) : tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    data,
    slots
}) => {
    // const updateExtProps = async (modprops: IModuleProps) => {
    //     const currentCartState = await getCartState(modprops.moduleProps.context?.actionContext);
    //     const cartLines = currentCartState?.cart?.CartLines;
    //     if (!cartLines || (cartLines && cartLines.length === 0)) {
    //         console.log(currentCartState?.cart?.CartLines?.length);
    //         const removeExtProps = [
    //             {
    //                 Key: 'ExtPropsRemove_CAP',
    //                 Value: {
    //                     BooleanValue: true
    //                 }
    //             }
    //         ];
    //         await currentCartState.updateExtensionProperties({ newExtensionProperties: removeExtProps });
    //     }
    // };

    // React.useEffect(() => {
    //     void updateExtProps(moduleProps);
    // }, []);

    const setRemoveData = async (modprops: IModuleProps) => {
        const currentCartState = await getCartState(modprops.moduleProps.context?.actionContext);
        const extProps = currentCartState?.cart?.ExtensionProperties;

        // @ts-ignore
        const extPropsToRemove =
            extProps &&
            find(
                extProps as ExtensionProperty[],
                (obj: ExtensionProperty): boolean => obj.Key === 'ExtPropsRemove_CAP' && obj.Value?.BooleanValue === true
            );

        const cartLines = currentCartState?.cart?.CartLines;
        // console.log("setRemoveData function");
        if (!cartLines || (cartLines && cartLines.length === 0)) {
            // prettier-ignore
            // @ts-ignore
            const extKeysToRemove = ['ActiveCartId_CAP', 'CheckoutCartId_CAP', 'GreetingMessage_CAP', 'GreetingSign_CAP', 'billing', 'Type_CAP', 'Country_CAP', 'City_CAP', 'ZipCode_CAP', 'OrgName_CAP',
                'VatNum_CAP', 'FiscalCode_CAP', 'PEC_CAP', 'SDI_CAP', 'InvoiceCustAccount_CAP', 'Street_CAP', 'StreetNumber_CAP', 'FirstName_CAP', 'LastName_CAP'];
            const extPropsToEmpty = [];
            // console.log("Cleaning Extension properties of cart");
            if (extProps) {
                for (const propToRemove of extKeysToRemove) {
                    if (find(extProps, (obj: { Key: string }): boolean => obj.Key === propToRemove)) {
                        extPropsToEmpty.push({
                            Key: propToRemove,
                            Value: {}
                        });
                    }
                }
                if (extPropsToRemove) {
                    extPropsToEmpty.push({
                        Key: 'ExtPropsRemove_CAP',
                        Value: {
                            BooleanValue: false
                        }
                    });
                }
            }
            if (extPropsToEmpty.length !== 0) {
                await currentCartState.updateExtensionProperties({ newExtensionProperties: extPropsToEmpty });
            } else {
                // console.log("no extension property to clean found");
            }
        }
    };
    React.useEffect(() => {
        trackPurchase(data.orderHydration.result?.salesOrder, data.orderHydration.result?.products || []);

        void setRemoveData(moduleProps);
    }, []);

    const locale = moduleProps.moduleProps.context.request.locale;
    // @ts-ignore
    const salseOrderData = moduleProps?.moduleProps?.data?.orderHydration?.result?.salesOrder;
    const taxAmount = salseOrderData?.TaxAmount;
    const extraOrdersSummaryEu = {
        taxAmount: taxAmount,
        locale: locale
    };
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} />}
            {payment && <OrderConfirmationPayment {...payment} />}
            {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} {...extraOrdersSummaryEu} />}
            {help && <OrderConfirmationHelp {...help} />}
            {slots &&
                slots.commisionJunction &&
                React.cloneElement(slots.commisionJunction[0] as React.ReactElement, {
                    salesOrder: data.orderHydration.result?.salesOrder
                })}
        </Module>
    );
};

export default OrderConfirmationView;
